import { useNavigate } from 'react-router-dom';
import type { LeaderboardRow as LeaderboardRowData } from '@wineryxplorer/shared';
import { Typography } from '../../components/ui/Typography';

interface LeaderboardRowProps {
  row: LeaderboardRowData;
  /** When true, render with the viewer-rank emphasis (burgundy left border). */
  emphasis?: boolean;
}

/**
 * Single rank row. Whole row navigates to the user's public profile.
 * The "Last visited <venue>" line contains an inline link that navigates
 * to the venue page instead — pointer events on that link stop propagation
 * so the row-level navigation does not win.
 */
export const LeaderboardRow = ({ row, emphasis = false }: LeaderboardRowProps) => {
  const navigate = useNavigate();

  const goToProfile = () => {
    navigate(`/users/${encodeURIComponent(row.userName)}`);
  };

  const initials = row.displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('') || '?';

  // Top-three ranks render the numeral larger and in the editorial serif —
  // the "podium" treatment without medals.
  const rankClass = row.rank <= 3 ? 'font-serif text-3xl' : 'font-serif text-xl';

  return (
    <div
      onClick={goToProfile}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToProfile();
        }
      }}
      tabIndex={0}
      role="link"
      aria-label={`View ${row.displayName}'s profile (rank ${row.rank}, ${row.uniqueVenueCount} wineries)`}
      className={`group flex items-center gap-4 px-3 md:px-4 py-3 border-b border-gray-200 cursor-pointer transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:bg-gray-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-700 ${
        emphasis ? 'border-l-4 border-l-secondary-700' : ''
      }`}
    >
      {/* Rank */}
      <div className={`flex-shrink-0 w-10 md:w-12 text-right text-gray-900 ${rankClass}`}>
        {row.rank}
      </div>

      {/* Avatar */}
      <div className="flex-shrink-0">
        {row.avatarUrl ? (
          <img
            src={row.avatarUrl}
            alt=""
            className="w-10 h-10 rounded-full object-cover bg-gray-100"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-sans text-sm font-medium text-gray-600">
            {initials}
          </div>
        )}
      </div>

      {/* Name + most-recent line */}
      <div className="flex-1 min-w-0">
        <div className="font-sans font-medium text-gray-900 truncate">
          {row.displayName}
        </div>
        {row.mostRecentCheckIn && (
          <div className="font-sans text-xs text-gray-600 truncate mt-0.5">
            Last visited{' '}
            {row.mostRecentCheckIn.venueName ? (
              <span
                role="link"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/wineries/${row.mostRecentCheckIn!.venueId}`);
                }}
                className="text-secondary-700 underline-offset-2 hover:underline cursor-pointer"
              >
                {row.mostRecentCheckIn.venueName}
              </span>
            ) : (
              <span className="italic text-gray-500">a winery</span>
            )}
          </div>
        )}
      </div>

      {/* Count */}
      <div className="flex-shrink-0 text-right">
        <Typography variant="venueName" as="div" className="text-gray-900 leading-none">
          {row.uniqueVenueCount}
        </Typography>
        <div className="font-sans text-xs text-gray-600 mt-0.5">
          {row.uniqueVenueCount === 1 ? 'winery' : 'wineries'}
        </div>
      </div>

      {/* Chevron affordance */}
      <div
        className="flex-shrink-0 text-gray-400 group-hover:text-primary-700 group-focus-visible:text-primary-700 transition-colors"
        aria-hidden="true"
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 5l6 5-6 5" />
        </svg>
      </div>
    </div>
  );
};
